import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Droplets, Thermometer, FlaskConical, Pill, Brain, Moon, ArrowRight, Check, Share2, Download, Save, RotateCcw, Zap, Clock, DollarSign, TrendingUp, ChevronDown, Star, ExternalLink, Activity } from 'lucide-react';
import { useResults } from '../lib/useResults';

const MODALITIES = [
  {
    id: 'hydrotherapy',
    name: 'Oxygen NanoBubble Therapy',
    shortName: 'HydroTherapy',
    category: 'Recovery',
    icon: Droplets,
    color: '#00B4D8',
    effectiveness: 9,
    timePerSession: '30-45 min',
    frequency: '3-5x/week',
    costRange: '$50-100/session or $9,650 home unit',
    difficulty: 'Easy',
    difficultyScore: 1,
    benefits: ['46% faster recovery (Rice University)', 'Direct oxygen to muscles via skin', 'Reduces inflammation without immune suppression', 'Warm & comfortable (78-104°F)'],
    bestFor: ['Post-workout recovery', 'Injury rehabilitation', 'Tissue repair'],
    link: '/hydrotherapy',
    linkLabel: 'HydroTherapy Hub',
    scienceNote: 'Bimini NanoJet delivers 300M+ O₂ nanobubbles/mL directly through skin pores.',
  },
  {
    id: 'cold-exposure',
    name: 'Cold Exposure / Cold Plunge',
    shortName: 'Cold Plunge',
    category: 'Recovery',
    icon: Thermometer,
    color: '#60a5fa',
    effectiveness: 6,
    timePerSession: '3-15 min',
    frequency: '3-7x/week',
    costRange: '$25-50/session or $1,500-8,000 home unit',
    difficulty: 'Hard',
    difficultyScore: 3,
    benefits: ['Dopamine boost (250-300% increase)', 'Mental toughness training', 'Reduces perceived soreness', 'Boosts norepinephrine'],
    bestFor: ['Mental resilience', 'Dopamine & mood', 'Acute soreness relief'],
    link: '/blog?category=Biohacking',
    linkLabel: 'Biohacking Articles',
    scienceNote: 'Note: Dr. Gabe Mirkin (inventor of R.I.C.E.) recanted — cold restricts blood flow and may delay healing.',
  },
  {
    id: 'peptides',
    name: 'Peptide Protocols',
    shortName: 'Peptides',
    category: 'Biohacking',
    icon: FlaskConical,
    color: '#E63946',
    effectiveness: 8,
    timePerSession: '5 min (injection)',
    frequency: 'Daily or cycling',
    costRange: '$50-300/month',
    difficulty: 'Medium',
    difficultyScore: 2,
    benefits: ['Accelerated tissue repair (BPC-157)', 'Enhanced recovery (TB-500)', 'Improved body composition (CJC/Ipamorelin)', 'Gut healing & systemic repair'],
    bestFor: ['Injury healing', 'Body composition', 'Anti-aging & longevity'],
    link: '/blog?category=Peptides',
    linkLabel: 'Peptide Guides',
    tools: [
      { label: 'Peptide Dosing Calculator', href: '/tools/peptide-dosing' },
      { label: 'Reconstitution Calculator', href: '/tools/peptide-calculator' },
    ],
    scienceNote: 'BPC-157 and TB-500 are the most researched recovery peptides with strong preclinical data.',
  },
  {
    id: 'supplements',
    name: 'Core Supplements',
    shortName: 'Supplements',
    category: 'Nutrition',
    icon: Pill,
    color: '#22c55e',
    effectiveness: 5,
    timePerSession: '2 min',
    frequency: 'Daily',
    costRange: '$30-100/month',
    difficulty: 'Easy',
    difficultyScore: 1,
    benefits: ['Creatine: strength & cognitive function', 'Magnesium: sleep & recovery', 'Omega-3: inflammation reduction', 'Vitamin D: immune & hormone support'],
    bestFor: ['Foundation health', 'Sleep quality', 'Joint & brain health'],
    link: '/blog?category=Biohacking',
    linkLabel: 'Supplement Articles',
    scienceNote: 'Creatine (5g/day) and Vitamin D (2000-5000 IU) have the strongest evidence base.',
  },
  {
    id: 'meditation',
    name: 'Meditation & Breathwork',
    shortName: 'Meditation',
    category: 'Wellness',
    icon: Brain,
    color: '#a78bfa',
    effectiveness: 7,
    timePerSession: '10-30 min',
    frequency: 'Daily',
    costRange: 'Free',
    difficulty: 'Easy',
    difficultyScore: 1,
    benefits: ['Reduces cortisol & stress hormones', 'Improves HRV & parasympathetic tone', 'Enhanced focus & reaction time', 'Better sleep quality'],
    bestFor: ['Stress management', 'Mental performance', 'Recovery optimization'],
    link: '/blog?category=Meditation',
    linkLabel: 'Meditation Guides',
    tools: [
      { label: 'Focus & Recovery Timers', href: '/timers' },
    ],
    scienceNote: 'NSDR (Non-Sleep Deep Rest) and box breathing shown to accelerate recovery via vagal tone.',
  },
  {
    id: 'sleep',
    name: 'Sleep Optimization',
    shortName: 'Sleep',
    category: 'Wellness',
    icon: Moon,
    color: '#818cf8',
    effectiveness: 10,
    timePerSession: '7-9 hours',
    frequency: 'Nightly',
    costRange: '$0-200/month (tracker optional)',
    difficulty: 'Medium',
    difficultyScore: 2,
    benefits: ['HGH release peaks during deep sleep', 'Muscle protein synthesis activation', 'Immune system restoration', 'Cognitive consolidation & memory'],
    bestFor: ['Everything — the #1 recovery tool', 'Hormone optimization', 'Athletic performance'],
    link: '/blog?category=Biohacking',
    linkLabel: 'Sleep Articles',
    scienceNote: 'Sleep is the single most impactful recovery variable. Even 1 hour less reduces performance by 10-30%.',
  },
];

const SCHEDULE_TEMPLATES = {
  minimal: { label: 'Minimal (3 days)', days: ['Mon', 'Wed', 'Fri'] },
  balanced: { label: 'Balanced (5 days)', days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] },
  intensive: { label: 'Full Send (7 days)', days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
};

const generateSchedule = (selected, template) => {
  const days = SCHEDULE_TEMPLATES[template].days;
  const mods = MODALITIES.filter(m => selected.includes(m.id));
  return days.map(day => {
    const dayMods = [];
    mods.forEach(m => {
      if (m.id === 'sleep' || m.id === 'supplements') {
        dayMods.push({ name: m.shortName, color: m.color, time: m.id === 'sleep' ? 'Night' : 'Morning' });
      } else if (m.id === 'meditation') {
        dayMods.push({ name: m.shortName, color: m.color, time: 'Morning' });
      } else {
        dayMods.push({ name: m.shortName, color: m.color, time: 'Post-Workout' });
      }
    });
    return { day, modalities: dayMods };
  });
};

const calcStackScore = (selected) => {
  if (selected.length === 0) return 0;
  const mods = MODALITIES.filter(m => selected.includes(m.id));
  const categories = new Set(mods.map(m => m.category));
  const avgEffectiveness = mods.reduce((s, m) => s + m.effectiveness, 0) / mods.length;
  const diversityBonus = (categories.size - 1) * 5;
  // eslint-disable-next-line no-nested-ternary -- readable tier picker
  const synergyBonus = (selected.includes('hydrotherapy') && selected.includes('peptides')) ? 8
    : (selected.includes('cold-exposure') && selected.includes('meditation')) ? 5 : 0;
  const sleepBonus = selected.includes('sleep') ? 10 : 0;
  const raw = (avgEffectiveness * 6) + diversityBonus + synergyBonus + sleepBonus + (mods.length * 2);
  return Math.min(100, Math.round(raw));
};

const getScoreLabel = (score) => {
  if (score >= 90) return { label: 'Elite', color: '#e8ff00' };
  if (score >= 75) return { label: 'Advanced', color: '#22c55e' };
  if (score >= 55) return { label: 'Solid', color: '#00B4D8' };
  if (score >= 30) return { label: 'Starter', color: '#f59e0b' };
  return { label: 'Build Your Stack', color: '#71717a' };
};

const getMonthlyEstimate = (selected) => {
  const estimates = { 'hydrotherapy': 200, 'cold-exposure': 100, 'peptides': 150, 'supplements': 60, 'meditation': 0, 'sleep': 50 };
  return selected.reduce((s, id) => s + (estimates[id] || 0), 0);
};

const getWeeklyTime = (selected) => {
  const mins = { 'hydrotherapy': 150, 'cold-exposure': 45, 'peptides': 35, 'supplements': 14, 'meditation': 140, 'sleep': 0 };
  return selected.reduce((s, id) => s + (mins[id] || 0), 0);
};

export default function RecoveryStackPage() {
  const [selected, setSelected] = useState([]);
  const [scheduleTemplate, setScheduleTemplate] = useState('balanced');
  const [showSchedule, setShowSchedule] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const cardRef = useRef(null);
  const { saveResult } = useResults();

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const score = calcStackScore(selected);
  const scoreInfo = getScoreLabel(score);
  const monthlyCost = getMonthlyEstimate(selected);
  const weeklyMins = getWeeklyTime(selected);
  const schedule = generateSchedule(selected, scheduleTemplate);

  const handleSave = useCallback(() => {
    const selectedMods = MODALITIES.filter(m => selected.includes(m.id));
    saveResult('recoveryStack', {
      value: score,
      label: scoreInfo.label,
      protocols: selectedMods.map(m => m.shortName).join(', '),
      monthlyCost,
      weeklyMinutes: weeklyMins,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, [selected, score, scoreInfo, monthlyCost, weeklyMins, saveResult]);

  const handleShare = useCallback(async () => {
    const selectedMods = MODALITIES.filter(m => selected.includes(m.id));
    const text = `My Recovery Stack (Score: ${score}/100 - ${scoreInfo.label})\n\n${selectedMods.map(m => `${m.shortName}: ${m.benefits[0]}`).join('\n')}\n\nBuild yours at ninjathlete.com/tools/recovery-stack`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My Recovery Stack', text });
      } else {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [selected, score, scoreInfo]);

  const handleReset = () => {
    setSelected([]);
    setShowSchedule(false);
  };

  return (
    <main className="pt-20 min-h-screen" data-testid="recovery-stack-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0a0a0a] to-black" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, rgba(0,180,216,0.3) 0%, transparent 50%), radial-gradient(circle at 30% 70%, rgba(230,57,70,0.2) 0%, transparent 50%)' }} />
        <div className="container-ninja relative z-10 py-16">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#E63946] text-white text-xs font-heading tracking-widest uppercase mb-6" data-testid="stack-badge">
              <Shield className="w-3.5 h-3.5" /> Recovery Protocol Builder
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl text-white font-heading leading-[0.95] mb-5" data-testid="stack-hero-title">
              BUILD YOUR <span className="text-[#00F0FF]">RECOVERY</span> STACK
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl max-w-xl mb-6">
              Select your recovery protocols, get a personalized weekly schedule, and see your stack score. Science-backed. No fluff.
            </p>
            <p className="text-zinc-500 text-sm">Select modalities below to build your protocol.</p>
          </div>
        </div>
      </section>

      {/* Stack Score Bar */}
      <div className="sticky top-16 md:top-20 z-40 bg-black/90 backdrop-blur-md border-b border-zinc-800" data-testid="stack-score-bar">
        <div className="container-ninja py-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 text-xs uppercase tracking-wider font-heading">Stack Score</span>
                <span className="text-2xl font-heading" style={{ color: scoreInfo.color }} data-testid="stack-score-value">{score}</span>
                <span className="text-zinc-600 text-sm font-heading">/100</span>
                <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-heading border" style={{ color: scoreInfo.color, borderColor: `${scoreInfo.color}40`, backgroundColor: `${scoreInfo.color}10` }} data-testid="stack-score-label">{scoreInfo.label}</span>
              </div>
              {selected.length > 0 && (
                <div className="hidden md:flex items-center gap-4 text-xs text-zinc-500">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {weeklyMins > 0 ? `${Math.round(weeklyMins / 60 * 10) / 10}hr/week` : 'N/A'}</span>
                  <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ~${monthlyCost}/mo</span>
                  <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> {selected.length} protocols</span>
                </div>
              )}
            </div>
            {selected.length > 0 && (
              <div className="flex items-center gap-2">
                <button onClick={handleSave} className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-heading uppercase tracking-wider hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors flex items-center gap-1.5" data-testid="save-stack-btn">
                  {saved ? <><Check className="w-3 h-3" /> Saved</> : <><Save className="w-3 h-3" /> Save</>}
                </button>
                <button onClick={handleShare} className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-heading uppercase tracking-wider hover:border-[#E63946] hover:text-[#E63946] transition-colors flex items-center gap-1.5" data-testid="share-stack-btn">
                  {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Share2 className="w-3 h-3" /> Share</>}
                </button>
                <button onClick={handleReset} className="px-3 py-1.5 text-zinc-500 text-xs font-heading uppercase tracking-wider hover:text-white transition-colors flex items-center gap-1.5" data-testid="reset-stack-btn">
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
              </div>
            )}
          </div>
          {/* Score progress bar */}
          <div className="mt-2 h-1 bg-zinc-800 overflow-hidden">
            <div className="h-full transition-all duration-500" style={{ width: `${score}%`, backgroundColor: scoreInfo.color }} data-testid="stack-score-progress" />
          </div>
        </div>
      </div>

      {/* Modality Cards */}
      <section className="container-ninja section-spacing" data-testid="modality-selection">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#00F0FF] text-xs uppercase tracking-wider font-heading mb-3">Select Your Protocols</p>
          <h2 className="text-2xl md:text-3xl text-white font-heading mb-2">Recovery Modalities</h2>
          <p className="text-zinc-500 text-sm mb-8">Click to add or remove from your stack. Each modality is rated by science-backed effectiveness.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {MODALITIES.map(mod => {
              const isSelected = selected.includes(mod.id);
              return (
                <div key={mod.id}
                  className={`relative bg-zinc-900/80 border p-5 transition-all duration-300 cursor-pointer group ${
                    isSelected ? 'border-opacity-60' : 'border-zinc-800 hover:border-opacity-30'
                  }`}
                  style={{ borderColor: isSelected ? mod.color : undefined }}
                  onClick={() => toggle(mod.id)}
                  data-testid={`modality-${mod.id}`}
                >
                  {/* Selected indicator */}
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center" style={{ backgroundColor: mod.color }} data-testid={`modality-${mod.id}-selected`}>
                      <Check className="w-4 h-4 text-black" />
                    </div>
                  )}

                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center border flex-shrink-0" style={{ borderColor: `${mod.color}40`, backgroundColor: `${mod.color}10` }}>
                      <mod.icon className="w-5 h-5" style={{ color: mod.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-heading text-lg">{mod.name}</h3>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider font-heading px-2 py-0.5 border mb-2 inline-block" style={{ color: mod.color, borderColor: `${mod.color}30` }}>{mod.category}</span>

                      {/* Effectiveness bar */}
                      <div className="flex items-center gap-2 my-3">
                        <span className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading w-20">Effectiveness</span>
                        <div className="flex-1 h-1.5 bg-zinc-800 overflow-hidden">
                          <div className="h-full" style={{ width: `${mod.effectiveness * 10}%`, backgroundColor: mod.color }} />
                        </div>
                        <span className="text-sm font-heading w-8 text-right" style={{ color: mod.color }}>{mod.effectiveness}/10</span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-[10px] mb-3">
                        <div><span className="text-zinc-600 uppercase tracking-wider block">Time</span><span className="text-zinc-300">{mod.timePerSession}</span></div>
                        <div><span className="text-zinc-600 uppercase tracking-wider block">Frequency</span><span className="text-zinc-300">{mod.frequency}</span></div>
                        <div><span className="text-zinc-600 uppercase tracking-wider block">Difficulty</span><span className="text-zinc-300">{mod.difficulty}</span></div>
                      </div>

                      <ul className="space-y-1 mb-3">
                        {mod.benefits.slice(0, 3).map(b => (
                          <li key={b} className="flex items-start gap-1.5 text-zinc-400 text-xs">
                            <Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: mod.color }} />{b}
                          </li>
                        ))}
                      </ul>

                      <p className="text-zinc-600 text-[10px] italic mb-2">{mod.scienceNote}</p>

                      <div className="flex items-center gap-3 text-xs" onClick={e => e.stopPropagation()}>
                        <Link to={mod.link} className="text-zinc-500 hover:text-white transition-colors flex items-center gap-1" data-testid={`modality-${mod.id}-link`}>
                          {mod.linkLabel} <ExternalLink className="w-3 h-3" />
                        </Link>
                        {mod.tools?.map(t => (
                          <Link key={t.href} to={t.href} className="text-zinc-500 hover:text-white transition-colors flex items-center gap-1">
                            {t.label} <ArrowRight className="w-3 h-3" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stack Summary & Schedule */}
      {selected.length > 0 && (
        <section className="bg-[#050505] section-spacing" data-testid="stack-summary-section">
          <div className="container-ninja max-w-5xl">
            <p className="text-[#E63946] text-xs uppercase tracking-wider font-heading mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4" /> Your Stack
            </p>
            <h2 className="text-2xl md:text-3xl text-white font-heading mb-6">Recovery Stack Summary</h2>

            {/* Summary Card */}
            <div ref={cardRef} className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8 mb-6" data-testid="stack-summary-card">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading">Stack Score</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-heading" style={{ color: scoreInfo.color }}>{score}</span>
                    <span className="text-zinc-600 font-heading">/100</span>
                    <span className="px-2 py-0.5 text-xs font-heading" style={{ color: scoreInfo.color, backgroundColor: `${scoreInfo.color}15` }}>{scoreInfo.label}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex flex-col gap-1 text-xs text-zinc-400">
                    <span><strong className="text-white">{selected.length}</strong> protocols</span>
                    <span><strong className="text-white">~{Math.round(weeklyMins / 60 * 10) / 10}hr</strong>/week</span>
                    <span><strong className="text-white">~${monthlyCost}</strong>/month</span>
                  </div>
                </div>
              </div>

              {/* Selected modalities */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {MODALITIES.filter(m => selected.includes(m.id)).map(mod => (
                  <div key={mod.id} className="flex items-center gap-2 bg-zinc-800/50 border border-zinc-700 p-3">
                    <mod.icon className="w-4 h-4 flex-shrink-0" style={{ color: mod.color }} />
                    <div>
                      <p className="text-white text-sm font-heading">{mod.shortName}</p>
                      <p className="text-zinc-500 text-[10px]">{mod.effectiveness}/10 effectiveness</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Synergy notes */}
              <div className="mt-4 space-y-2">
                {selected.includes('hydrotherapy') && selected.includes('peptides') && (
                  <div className="flex items-start gap-2 text-xs bg-[#00B4D8]/5 border border-[#00B4D8]/15 p-3">
                    <Star className="w-4 h-4 text-[#00B4D8] flex-shrink-0 mt-0.5" />
                    <p className="text-zinc-300"><strong className="text-[#00B4D8]">Synergy Bonus:</strong> HydroTherapy + Peptides = enhanced oxygen delivery accelerates peptide absorption and tissue repair.</p>
                  </div>
                )}
                {selected.includes('cold-exposure') && selected.includes('meditation') && (
                  <div className="flex items-start gap-2 text-xs bg-[#a78bfa]/5 border border-[#a78bfa]/15 p-3">
                    <Star className="w-4 h-4 text-[#a78bfa] flex-shrink-0 mt-0.5" />
                    <p className="text-zinc-300"><strong className="text-[#a78bfa]">Synergy Bonus:</strong> Cold Exposure + Meditation = breathwork during cold builds resilience and improves vagal tone.</p>
                  </div>
                )}
                {!selected.includes('sleep') && (
                  <div className="flex items-start gap-2 text-xs bg-[#f59e0b]/5 border border-[#f59e0b]/15 p-3">
                    <TrendingUp className="w-4 h-4 text-[#f59e0b] flex-shrink-0 mt-0.5" />
                    <p className="text-zinc-300"><strong className="text-[#f59e0b]">Tip:</strong> Adding Sleep Optimization would significantly boost your stack score. Sleep is the #1 recovery tool.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Weekly Schedule */}
            <div className="mb-6">
              <button onClick={() => setShowSchedule(!showSchedule)}
                className="w-full flex items-center justify-between bg-zinc-900/80 border border-zinc-800 hover:border-zinc-700 p-4 transition-colors"
                data-testid="toggle-schedule-btn">
                <span className="text-white font-heading text-sm uppercase tracking-wider">Weekly Schedule</span>
                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showSchedule ? 'rotate-180' : ''}`} />
              </button>

              {showSchedule && (
                <div className="bg-zinc-900/50 border border-zinc-800 border-t-0 p-5" data-testid="schedule-section">
                  <div className="flex items-center gap-3 mb-4">
                    {Object.entries(SCHEDULE_TEMPLATES).map(([key, tmpl]) => (
                      <button key={key} onClick={() => setScheduleTemplate(key)}
                        className={`px-3 py-1.5 text-xs font-heading uppercase tracking-wider border transition-colors ${
                          scheduleTemplate === key ? 'text-[#00F0FF] border-[#00F0FF]/40 bg-[#00F0FF]/5' : 'text-zinc-500 border-zinc-700 hover:text-white'
                        }`}
                        data-testid={`schedule-${key}`}>
                        {tmpl.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid gap-2">
                    {schedule.map(day => (
                      <div key={day.day} className="flex items-center gap-3 bg-zinc-800/50 p-3">
                        <span className="text-white font-heading text-sm w-10">{day.day}</span>
                        <div className="flex flex-wrap gap-2">
                          {day.modalities.map((m) => (
                            <span key={m.name} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border" style={{ color: m.color, borderColor: `${m.color}30`, backgroundColor: `${m.color}08` }}>
                              {m.name} <span className="text-zinc-600">({m.time})</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3">
              <button onClick={handleSave}
                className="px-6 py-3 bg-[#00F0FF] text-black font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity flex items-center gap-2"
                data-testid="save-stack-btn-bottom">
                {saved ? <><Check className="w-4 h-4" /> Saved to Dashboard</> : <><Save className="w-4 h-4" /> Save to My Results</>}
              </button>
              <button onClick={handleShare}
                className="px-6 py-3 border border-[#E63946]/40 text-[#E63946] font-heading text-sm uppercase tracking-wider hover:bg-[#E63946]/10 transition-colors flex items-center gap-2"
                data-testid="share-stack-btn-bottom">
                {copied ? <><Check className="w-4 h-4" /> Copied to Clipboard</> : <><Share2 className="w-4 h-4" /> Share My Stack</>}
              </button>
              <Link to="/my-results" className="px-6 py-3 border border-zinc-700 text-zinc-400 font-heading text-sm uppercase tracking-wider hover:border-zinc-500 hover:text-white transition-colors flex items-center gap-2" data-testid="view-results-link">
                View My Results <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Cross-link CTA */}
      <section className="container-ninja section-spacing">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl text-white font-heading mb-6">Explore Recovery Science</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/hydrotherapy" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#00B4D8]/40 p-5 transition-all" data-testid="crosslink-hydrotherapy">
              <Droplets className="w-6 h-6 text-[#00B4D8] mb-3" />
              <h3 className="text-white font-heading text-lg group-hover:text-[#00B4D8] transition-colors">HydroTherapy Hub</h3>
              <p className="text-zinc-500 text-sm mt-1">Bimini NanoJet, comparison guides, ROI calculator for gyms.</p>
              <span className="text-[#00B4D8] text-xs font-heading uppercase tracking-wider mt-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Explore <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
            <Link to="/hyrox" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#e8ff00]/40 p-5 transition-all" data-testid="crosslink-hyrox">
              <Zap className="w-6 h-6 text-[#e8ff00] mb-3" />
              <h3 className="text-white font-heading text-lg group-hover:text-[#e8ff00] transition-colors">HYROX Hub</h3>
              <p className="text-zinc-500 text-sm mt-1">Training guides, race tools, gym finder, and equipment.</p>
              <span className="text-[#e8ff00] text-xs font-heading uppercase tracking-wider mt-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Explore <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
            <Link to="/tools" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#E63946]/40 p-5 transition-all" data-testid="crosslink-tools">
              <Shield className="w-6 h-6 text-[#E63946] mb-3" />
              <h3 className="text-white font-heading text-lg group-hover:text-[#E63946] transition-colors">All Tools</h3>
              <p className="text-zinc-500 text-sm mt-1">Calculators for nutrition, training, peptides, and more.</p>
              <span className="text-[#E63946] text-xs font-heading uppercase tracking-wider mt-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Explore <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
